import React, { useMemo } from 'react'
import { Alert, Box, Chip, Paper, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { getLegStatus } from '@otw/models/logistics'
import { useLogistics } from '@/hooks/logistics/useLogistics'
import { useLegPhotosStatusForPlans } from '@/hooks/logistics/useLegPhotosStatus'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { legStatusTone } from '../../utils/mobileStatusTones'
import useAdminTransporter from 'hooks/admin/useAdminTransporter'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from 'hooks/inventory/useRepairShopLocations'

type MissingPhotoRow = {
  id: string
  planId: string
  truckId: string
  stockNumber: string
  vin: string
  legIndex: number
  fromLabel: string
  toLabel: string
  companyName: string
  missing: string
  legStatus: string
}

const MissingTransporterPhotosTab: React.FC = () => {
  const isMobile = useIsMobile()
  const { transportTrucksWithVehicles } = useLogistics()
  const { transporterGroups } = useAdminTransporter()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const location of pickupLocations ?? []) {
      if (!location.id) continue
      map.set(location.id, location.company_name || location.address || location.id)
    }
    for (const location of repairShopLocations ?? []) {
      if (!location.id) continue
      map.set(location.id, location.name || location.id)
    }
    return map
  }, [pickupLocations, repairShopLocations])

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const group of transporterGroups ?? []) {
      map.set(group.id, group.name || group.id)
    }
    return map
  }, [transporterGroups])

  const planIds = useMemo(
    () =>
      transportTrucksWithVehicles.flatMap(item =>
        item.shipmentPlans.map(plan => plan.id).filter(Boolean),
      ),
    [transportTrucksWithVehicles],
  )
  const statusByPlan = useLegPhotosStatusForPlans(planIds, planIds.length > 0)

  const rows = useMemo<MissingPhotoRow[]>(() => {
    const missing: MissingPhotoRow[] = []
    for (const { truck, vehicle, shipmentPlans } of transportTrucksWithVehicles) {
      for (const plan of shipmentPlans) {
        const legs = plan.shipment_legs ?? []
        const planStatus = statusByPlan.get(plan.id) ?? {}
        legs.forEach((leg, legIndex) => {
          const status = getLegStatus(leg)
          if (status === 'Draft' || leg.rejected) return
          const photos = planStatus[legIndex] ?? { hasPreDelivery: false, hasPostDelivery: false }
          const gaps: string[] = []
          if (!photos.hasPreDelivery) gaps.push('Pre-delivery')
          if (status === 'Completed' && !photos.hasPostDelivery) gaps.push('Post-delivery')
          if (gaps.length === 0) return
          missing.push({
            id: `${plan.id}-${legIndex}`,
            planId: plan.id,
            truckId: truck.id,
            stockNumber: vehicle?.stock_number || '—',
            vin: vehicle?.vin || '—',
            legIndex: legIndex + 1,
            fromLabel: locationNameById.get(leg.from_id) || leg.from_id,
            toLabel: locationNameById.get(leg.to_id) || leg.to_id,
            companyName: (leg.company_id && companyNameById.get(leg.company_id)) || 'Unassigned',
            missing: gaps.join(', '),
            legStatus: status,
          })
        })
      }
    }
    return missing
  }, [transportTrucksWithVehicles, statusByPlan, locationNameById, companyNameById])

  const columns: GridColDef<MissingPhotoRow>[] = [
    { field: 'stockNumber', headerName: 'Stock #', width: 120 },
    { field: 'vin', headerName: 'VIN', width: 180 },
    { field: 'companyName', headerName: 'Transporter', flex: 1, minWidth: 160 },
    { field: 'legIndex', headerName: 'Leg', width: 70 },
    { field: 'fromLabel', headerName: 'From', flex: 1, minWidth: 140 },
    { field: 'toLabel', headerName: 'To', flex: 1, minWidth: 140 },
    { field: 'legStatus', headerName: 'Status', width: 120 },
    {
      field: 'missing',
      headerName: 'Missing photos',
      width: 180,
      renderCell: params => <Chip size='small' color='warning' label={params.value} />,
    },
    { field: 'planId', headerName: 'Load / plan', width: 160 },
  ]

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant='h6' sx={{ fontWeight: 700, mb: 0.5 }}>
        Missing transporter photos
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Active legs missing required pre-delivery or post-delivery portal photos. Use this list to
        enforce the $250 policy outside the app. The portal does not bill or email automatically.
      </Typography>
      <Alert severity='info' sx={{ mb: 2 }}>
        Screenshot-only sets stay incomplete when the photo template still requires real angles.
      </Alert>
      <Paper variant='outlined'>
        {isMobile ? (
          <Box sx={{ p: 1.5 }}>
            <MobileRecordList isEmpty={rows.length === 0} empty='No missing photos.'>
              {rows.map(row => (
                <MobileRecordCard
                  key={row.id}
                  title={`${row.stockNumber} · VIN ${row.vin}`}
                  status={{ tone: legStatusTone(row.legStatus), label: row.legStatus }}
                  chips={[{ label: row.missing, tone: 'warning' }]}
                  meta={`${row.fromLabel} → ${row.toLabel}`}
                  detail={row.companyName}
                />
              ))}
            </MobileRecordList>
          </Box>
        ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={row => row.id}
          autoHeight
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
        />
        )}
      </Paper>
    </Box>
  )
}

export default MissingTransporterPhotosTab
